import { defineStore } from "pinia"

const yearCookieOptions = { maxAge: 60 * 60 * 24 * 365 }

interface UserState {
  user: IUser | null
  access_token: string
  forget_password_token: string
  register_token: string
  isLogin: boolean
  lang: string
  two_factor_token: string
  profileActiveTab: string
  missionCreateIsMyValue: boolean
  reUploadProofUserMissionId: number
  mobile_verification_required: boolean
  email_verification_required: boolean
  cookieStatus: boolean
  cookieReason: string
  changeRolePending: boolean
  phone: string
}

interface UserActions {
  setToken(access_token: string): void
  setRegisterToken(token: string): void
  setLang(lang: string): void
  setUser(user: IUser): void
  setPhone(data: string): void
  setTwoFactor(data: string): void
  setForgotToken(data: string): void
  login(): void
  register(): void
  removeAuthData(): void
  logout(): void
  initialize(): Promise<void>
  getMe(): Promise<IUser | null>
  changeRole(id: string): Promise<void>
  reset(): void
  setVerifyRequiredFields(fields: {
    mobile_verification_required: boolean
    email_verification_required: boolean
  }): void
}

interface UserGetters {
  [key: string]: (state: UserState) => any
  getAccessToken: (state: UserState) => string
  getIsLogin: (state: UserState) => boolean
  getRegisterToken: (state: UserState) => string
  getUser: (state: UserState) => IUser | null
  getPhone: (state: UserState) => string
  getFullname: (state: UserState) => string | null
  getRoles: (state: UserState) => IRole[]
  getLang: (state: UserState) => string
  getActiveRole: (state: UserState) => IRole | null
  getTwoFactor: (state: UserState) => string
  getPoints: (state: UserState) => number
  getMoneys: (state: UserState) => string
  checkPermissions: (state: UserState) => (permissionName: string) => boolean
}

export const useUserStore = defineStore<
  string,
  UserState,
  UserGetters,
  UserActions
>("userStore", {
  state: (): UserState => ({
    user: null,
    phone: "",
    access_token: "",
    forget_password_token: "",
    register_token: "",
    isLogin: false,
    lang: "tr",
    two_factor_token: "",
    profileActiveTab: "",
    missionCreateIsMyValue: false,
    reUploadProofUserMissionId: 0,
    mobile_verification_required: false,
    email_verification_required: false,
    cookieStatus: false,
    cookieReason: "",
    changeRolePending: false,
  }),
  getters: {
    getAccessToken: (state) => state.access_token,
    getIsLogin: (state) => state.isLogin,
    getRegisterToken: (state) => state.register_token,
    getUser: (state) => state.user,
    getPhone: (state) => state.phone,
    getFullname: (state) =>
      state.user ? `${state.user.name} ${state.user.surname}` : null,
    getRoles: (state) => state.user?.roles || [],
    getLang: (state) => state.lang,
    getActiveRole: (state) => state.user?.active_role || null,
    getTwoFactor: (state) => state.two_factor_token,
    getPoints: (state) => state.user?.points || 0,
    getMoneys: (state) => state.user?.balance || "0",
    checkPermissions: (state) => (permissionName: string) =>
      !permissionName ||
      state.user?.active_permissions.includes(permissionName) ||
      false,
  },
  actions: {
    async setToken(access_token: string) {
      this.access_token = access_token
      this.isLogin = true
      useCookie("access_token", yearCookieOptions).value = access_token
      useCookie("isLogin", yearCookieOptions).value = this.isLogin.toString()
    },
    setRegisterToken(token: string) {
      this.register_token = token
      useCookie("register_token").value = token
    },
    setLang(lang: string) {
      this.lang = lang
      // useCookie("lang", yearCookieOptions).value = lang
    },
    setUser(user: IUser) {
      this.user = user
      useLocalStorage("user", null).value = JSON.stringify(user)
    },
    setPhone(data: string) {
      this.phone = data
    },
    setTwoFactor(data: string) {
      this.two_factor_token = data
      useCookie("two_factor_token").value = data
    },
    setForgotToken(data: string) {
      this.forget_password_token = data
      useCookie("forget_password_token").value = data
    },
    login() {},
    register() {},
    removeAuthData() {
      this.access_token = ""
      this.register_token = ""
      this.isLogin = false
      this.user = null
      useCookie("access_token", yearCookieOptions).value = this.access_token
      useCookie("register_token").value = this.register_token
      useCookie("isLogin", yearCookieOptions).value = this.isLogin.toString()
      useLocalStorage("user", null).value = null
    },
    logout() {
      this.removeAuthData()
      const router = useRouter()
      const runtimeConfig = useRuntimeConfig()
      if (runtimeConfig?.public?.source == "customer") {
        router.push("/")
      } else if (process.client) {
        window.location.pathname = "/"
      }
    },
    async initialize() {
      this.access_token =
        useCookie("access_token", yearCookieOptions).value || ""
      this.isLogin = Boolean(useCookie("isLogin").value)
      this.user = JSON.parse(useLocalStorage("user", null).value || "null")
    },
    async getMe() {
      const { data } = await useCustomFetch(`/api/me`)
      if (data?.value?.data) {
        this.setUser(data.value.data)
        return data.value.data
      }
      return null
    },
    async changeRole(id: string) {
      this.changeRolePending = true
      const { data } = await useCustomFetch(
        `/api/set-active-role/${id}`,
      ).finally(() => {
        this.changeRolePending = false
      })
      if (!data?.value?.success) {
        useHandleError(data.value)
        return
      }

      await this.getMe()
      const router = useRouter()
      router.push("/home")
    },
    reset() {
      this.removeAuthData()
      this.$reset()
    },
    setVerifyRequiredFields({
      mobile_verification_required = false,
      email_verification_required = false,
    }: {
      mobile_verification_required: boolean
      email_verification_required: boolean
    }) {
      this.mobile_verification_required = mobile_verification_required
      this.email_verification_required = email_verification_required
    },
  },
  persist: [
    {
      pick: ["user"],
      storage: useLocalStorage,
    },
    {
      storage: useSessionStorage,
    },
  ],
})
